import { Component, EventEmitter, forwardRef, Input, OnChanges, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** A choice of a radio group; `example` shows after its label, as code (`ItemCode`). */
export interface RadioOption<T = any> {
  label: string;
  value: T;
  example?: string;
  disabled?: boolean;
}

/**
 * One choice among a few, with ngModel or a form control:
 *
 *     <gains-radio-group formControlName="naming" [options]="namingOptions" buttonStyle="button"></gains-radio-group>
 *
 * `buttonStyle="button"` shows them as joined buttons, `radio` as round radios.
 */
@Component({
  selector: 'gains-radio-group',
  standalone: false,
  template: `<nz-radio-group [ngModel]="value" (ngModelChange)="onSelect($event)" [nzDisabled]="disabled" role="radiogroup" [attr.aria-label]="ariaLabel ?? null">
    @for (option of options; track option.value) {
      @if (buttonStyle === 'button') {
        <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -- ng-zorro's label holds its own input -->
        <label nz-radio-button [nzValue]="option.value" [nzDisabled]="option.disabled ?? false"
          >{{ option.label }}
          @if (option.example) {
            <code>{{ option.example }}</code>
          }
        </label>
      } @else {
        <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -- ng-zorro's label holds its own input -->
        <label nz-radio [nzValue]="option.value" [nzDisabled]="option.disabled ?? false"
          >{{ option.label }}
          @if (option.example) {
            <code>{{ option.example }}</code>
          }
        </label>
      }
    }
  </nz-radio-group>`,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => GainsRadioGroupComponent), multi: true }]
})
export class GainsRadioGroupComponent implements ControlValueAccessor, OnChanges {
  @Input() options: RadioOption[] = [];
  @Input() buttonStyle: 'button' | 'radio' = 'radio';
  /** Disabled apart from its form, e.g. while saving. */
  @Input() disabledState = false;
  /** Names the group for screen readers when it has no label. */
  @Input() ariaLabel?: string;
  /** The value chosen by the user. */
  @Output() valueChange = new EventEmitter<any>();

  value: any = null;
  formDisabled = false;
  /** Disabled by its form or by `disabledState`. */
  disabled = false;
  private onChange: (value: any) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  ngOnChanges(): void {
    this.disabled = this.disabledState || this.formDisabled;
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled = isDisabled;
    this.disabled = this.disabledState || this.formDisabled;
  }

  onSelect(value: any): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
    this.valueChange.emit(value);
  }
}
